import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Search, Users, Clock, Shield, ShieldOff, Trash2, ShieldCheck, ArrowLeft, ChevronRight, Crown, Key } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from '../hooks/use-toast';
import { rosterAPI } from '../services/api';
import { format, parseISO } from 'date-fns';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import TeamGate from '../components/TeamGate';
import PermissionsModal from '../components/PermissionsModal';

const Roster = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isCoach = user?.role === 'coach' || user?.role === 'admin';

  const [searchQuery, setSearchQuery] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Permission Modal State
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    if (user?.team_id) {
      loadRoster();
    }
  }, [user?.team_id]);

  const loadRoster = async () => {
    try {
      setLoading(true);
      const response = await rosterAPI.getRoster(user.team_id);
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to load roster:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPermissions = (member) => {
    setSelectedMember(member);
    setPermissionModalOpen(true);
  };

  const handleSavePermissions = async (userId, data) => {
    try {
      await rosterAPI.updateMemberPermissions(user.team_id, userId, data);
      toast({ title: t('roster.successPermissions') || 'Yetkiler başarıyla güncellendi' });
      await loadRoster();
    } catch (error) {
      console.error('Failed to update permissions:', error);
      toast({
        title: t('roster.errorPermissions') || 'Yetkiler güncellenemedi',
        variant: 'destructive'
      });
    }
  };

  const handleKick = async (memberUserId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName} from the team? This action cannot be undone.`)) {
      return;
    }

    try {
      await rosterAPI.removeMember(user.team_id, memberUserId);
      toast({
        title: t('roster.memberRemoved') || "Üye çıkarıldı",
        description: `${memberName} ${t('roster.removedFromTeam') || 'takımdan çıkarıldı.'}`,
      });
      loadRoster();
    } catch (error) {
      const msg = error.response?.data?.detail || t('roster.errorRemove') || "Üye çıkarılamadı";
      toast({
        title: t('common.error'),
        description: msg,
        variant: "destructive"
      });
    }
  };

  const handlePositionRequest = async (positions) => {
    try {
      await rosterAPI.createPositionRequest(user.team_id, positions);
      toast({ title: t('roster.requestSent') || 'Pozisyon değiştirme isteği gönderildi' });
      setPermissionModalOpen(false);
    } catch (error) {
      console.error('Failed to request position change:', error);
      toast({
        title: t('common.error'),
        variant: 'destructive'
      });
    }
  };

  const handleTransferOwnership = async (newOwnerId, newOwnerName) => {
    if (!window.confirm(`Are you sure you want to transfer ownership to ${newOwnerName}? You will lose owner privileges!`)) {
      return;
    }
    try {
      await rosterAPI.transferOwnership(user.team_id, newOwnerId);
      toast({ title: t('common.success') || 'Success', description: "Ownership transferred successfully." });
      loadRoster();
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || "Transfer failed",
        variant: 'destructive'
      });
    }
  };

  const filteredMembers = members.filter(member =>
    member.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Find original coach (member with earliest created_at)
  const originalCoach = members.length > 0
    ? members.reduce((prev, current) =>
      (new Date(prev.created_at) < new Date(current.created_at) ? prev : current)
    )
    : null;

  const players = filteredMembers.filter(m => m.role === 'player');
  const coaches = filteredMembers.filter(m => m.role === 'coach');

  const getPlayerGroup = (positions) => {
    if (!positions || positions.length === 0) return 'Unassigned';
    const pos = positions[0];
    const offense = ['QB', 'HB', 'FB', 'WR', 'TE', 'OG', 'OT', 'C'];
    const defense = ['CB', 'S', 'DE', 'DT', 'MLB', 'OLB'];
    const special = ['K', 'P'];
    
    if (offense.includes(pos)) return 'Offense';
    if (defense.includes(pos)) return 'Defense';
    if (special.includes(pos)) return 'Special Teams';
    return 'Unassigned';
  };

  const groupedPlayers = {
    Offense: [],
    Defense: [],
    'Special Teams': [],
    Unassigned: []
  };

  players.forEach(p => {
    groupedPlayers[getPlayerGroup(p.positions)].push(p);
  });

  const getPositionBadge = (pos) => {
    // ... (keep existing colors object)
    const colors = {
      // Defensive Backs
      'CB': 'bg-blue-600', 'S': 'bg-blue-600',
      // Defensive Line
      'DE': 'bg-red-600', 'DT': 'bg-red-600',
      // Linebackers
      'MLB': 'bg-purple-600', 'OLB': 'bg-purple-600',
      // Running Backs
      'HB': 'bg-green-600', 'FB': 'bg-green-600',
      // Receivers
      'WR': 'bg-orange-500',
      'TE': 'bg-amber-500', // Between WR and OL
      // Offensive Line
      'OG': 'bg-slate-600', 'OT': 'bg-slate-600', 'C': 'bg-slate-600',
      // QB
      'QB': 'bg-yellow-500',
      // Special Teams
      'K': 'bg-pink-600', 'P': 'bg-pink-600'
    };

    return (
      <Badge
        key={pos}
        className={`${colors[pos] || 'bg-gray-600'} text-white border-none px-2 py-0 h-5 text-[10px] font-bold`}
      >
        {pos}
      </Badge>
    );
  };

  const MemberCard = ({ member, colorClass }) => (
    <div className="relative group flex items-center gap-4 bg-[#1a1a1b]/60 backdrop-blur-xl p-4 rounded-[2rem] border border-white/5 shadow-lg active:scale-[0.98] transition-all">
      <div className="relative">
        <Avatar className="h-14 w-14 rounded-2xl border border-white/10 p-0.5">
          <AvatarImage src={member.avatar_url} className="rounded-2xl" />
          <AvatarFallback className={cn("text-white font-black rounded-2xl", colorClass)}>
            {member.username.substring(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        {member.is_captain && (
          <div className="absolute -top-2 -right-2 bg-amber-500 p-1 rounded-lg border-2 border-[#1a1a1b] shadow-xl">
            <Crown className="w-3 h-3 text-white fill-current" />
          </div>
        )}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          {member.jersey_number && (
            <span className="text-snapp-green font-black tabular-nums text-lg">#{member.jersey_number}</span>
          )}
          <p className="font-black text-white text-base leading-tight">{member.username}</p>
          {(member.is_captain || (member.permissions && member.permissions.length > 0)) && (
            <ShieldCheck className={cn("w-4 h-4", member.role === 'coach' ? "text-blue-500" : "text-purple-500")} />
          )}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {member.positions && member.positions.length > 0
            ? member.positions.map(pos => getPositionBadge(pos))
            : <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">{member.role === 'coach' ? 'Staff' : 'Player'}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {(isCoach || (member.user_id === user?.id)) && (
          <button
            onClick={() => handleOpenPermissions(member)}
            className="p-3 bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white rounded-2xl transition-all active:scale-90"
          >
            <Shield className="w-5 h-5" />
          </button>
        )}
        {originalCoach && originalCoach.user_id === user?.id && member.role === 'coach' && member.id !== originalCoach.id && (
          <button
            onClick={() => handleTransferOwnership(member.user_id, member.username)}
            title="Transfer Ownership"
            className="p-3 bg-yellow-500/10 hover:bg-yellow-500 text-yellow-500 hover:text-white rounded-2xl transition-all active:scale-90"
          >
            <Key className="w-5 h-5" />
          </button>
        )}
        {isCoach && (!originalCoach || member.id !== originalCoach.id) && (
          <button
            onClick={() => handleKick(member.user_id, member.username)}
            className="p-3 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white rounded-2xl transition-all active:scale-90"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <TeamGate title="Team Roster">
      <div className="min-h-screen bg-[#0a0a0b] text-white pb-24 animate-in fade-in duration-500" data-testid="roster-page">
        {/* Mobile Sticky Header */}
        <div className="lg:hidden p-4 bg-[#0a0a0b]/80 backdrop-blur-xl sticky top-0 z-[100] border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors active:scale-90"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-black text-white tracking-tight uppercase">
              {t('roster.title')}
            </h2>
          </div>
          <div className="px-3 py-1 bg-snapp-purple/10 border border-snapp-purple/20 rounded-full">
            <span className="text-[10px] font-black text-snapp-purple uppercase tracking-widest">{members.length} {t('roster.members', 'Üye')}</span>
          </div>
        </div>

        <div className="p-4 lg:p-8 space-y-8">
          {/* Header & Search */}
          <div className="space-y-6">
            <div className="hidden lg:block">
              <h1 className="text-3xl font-black text-white uppercase tracking-tight">{t('roster.title')}</h1>
              <p className="text-gray-400 font-medium mt-1">{t('roster.subtitle')}</p>
            </div>

            <div className="relative group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 group-focus-within:text-snapp-purple transition-colors" />
              <input
                type="text"
                placeholder={t('roster.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[#1a1a1b] border border-white/5 focus:border-snapp-purple/30 rounded-2xl pl-12 pr-4 py-4 text-white placeholder:text-gray-600 outline-none transition-all shadow-xl font-medium"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-10 h-10 border-2 border-snapp-blue border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="space-y-12">
              {/* Coaches Section */}
              {coaches.length > 0 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 px-2">
                    <div className="p-2 bg-snapp-blue/10 rounded-xl">
                      <Shield className="w-5 h-5 text-snapp-blue" />
                    </div>
                    <div>
                      <h3 className="text-xl font-black text-white uppercase tracking-tight">{t('roster.staff') || 'Ekip'}</h3>
                      <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mt-0.5">Management & Coaching</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {coaches.map((member) => (
                      <MemberCard key={member.id} member={member} colorClass="bg-snapp-blue" />
                    ))}
                  </div>
                </div>
              )}

              {/* Players Section */}
              <div className="space-y-6">
                <div className="flex items-center gap-3 px-2">
                  <div className="p-2 bg-snapp-purple/10 rounded-xl">
                    <Users className="w-5 h-5 text-snapp-purple" />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-white uppercase tracking-tight">{t('dashboard.players')}</h3>
                    <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mt-0.5">Active Roster</p>
                  </div>
                </div>

                {players.length === 0 ? (
                  <div className="text-center py-20 bg-[#1a1a1b]/30 rounded-[2.5rem] border border-white/5 border-dashed">
                    <Users className="w-16 h-16 text-white/5 mx-auto mb-4" />
                    <p className="text-gray-500 font-bold uppercase tracking-widest text-xs">{t('roster.noPlayers')}</p>
                  </div>
                ) : (
                  <div className="space-y-8">
                    {Object.entries(groupedPlayers).map(([groupName, groupPlayers]) => {
                      if (groupPlayers.length === 0) return null;
                      
                      return (
                        <div key={groupName} className="space-y-3">
                          <h4 className="text-sm font-black text-white/60 uppercase tracking-widest px-2 border-b border-white/10 pb-2">
                            {t(`roster.groups.${groupName.toLowerCase().replace(' ', '')}`, groupName)} ({groupPlayers.length})
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {groupPlayers.map((member) => (
                              <MemberCard key={member.id} member={member} colorClass="bg-snapp-purple" />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <PermissionsModal
        open={permissionModalOpen}
        onOpenChange={setPermissionModalOpen}
        member={selectedMember}
        onSave={(!isCoach && selectedMember?.user_id === user?.id) ? handlePositionRequest : handleSavePermissions}
        teamId={user?.team_id}
        requestMode={!isCoach && selectedMember?.user_id === user?.id}
      />
    </TeamGate>
  );
};

export default Roster;
